import { useState } from "react";

export interface GraphPoint {
  scoreLeadBlack: number | null; // null when that position has not been analyzed yet
  label: string;
}

interface ScoreGraphProps {
  points: GraphPoint[];
  currentIndex: number;
  swings: number[]; // indices flagged as the biggest swings
  onSelect: (index: number) => void;
}

const WIDTH = 320;
const HEIGHT = 64;
const PAD_X = 6;
const PAD_Y = 6;

export function formatScoreLead(value: number): string {
  if (Math.abs(value) < 0.05) return "Even";
  return `${value > 0 ? "Black" : "White"} +${Math.abs(value).toFixed(1)}`;
}

export function ScoreGraph({ points, currentIndex, swings, onSelect }: ScoreGraphProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const n = points.length;
  const innerW = WIDTH - PAD_X * 2;
  const innerH = HEIGHT - PAD_Y * 2;
  const swingSet = new Set(swings);

  const x = (i: number) => (n <= 1 ? WIDTH / 2 : PAD_X + (i / (n - 1)) * innerW);
  const maxAbsScore = Math.max(
    5,
    Math.ceil(Math.max(...points.map((point) => Math.abs(point.scoreLeadBlack ?? 0))) / 5) * 5
  );
  const y = (scoreLead: number) => PAD_Y + ((maxAbsScore - scoreLead) / (maxAbsScore * 2)) * innerH;
  const hoveredPoint = hoveredIndex === null ? null : points[hoveredIndex];
  const hoveredLeft =
    hoveredIndex === null || n === 0 ? 50 : Math.min(88, Math.max(12, (x(hoveredIndex) / WIDTH) * 100));

  // Build polyline segments, breaking the line across un-analyzed (null) gaps.
  const segments: string[] = [];
  let current: string[] = [];
  points.forEach((point, i) => {
    if (point.scoreLeadBlack === null) {
      if (current.length > 1) segments.push(current.join(" "));
      current = [];
      return;
    }
    current.push(`${x(i).toFixed(1)},${y(point.scoreLeadBlack).toFixed(1)}`);
  });
  if (current.length > 1) segments.push(current.join(" "));

  return (
    <div className="score-graph" aria-label="Score lead over the game">
      {hoveredPoint?.scoreLeadBlack !== null && hoveredPoint?.scoreLeadBlack !== undefined ? (
        <div className="wg-tooltip" style={{ left: `${hoveredLeft}%` }}>
          {hoveredPoint.label} · {formatScoreLead(hoveredPoint.scoreLeadBlack)}
        </div>
      ) : null}
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img">
        {/* Black-favoured above the midline, White-favoured below. */}
        <rect x="0" y="0" width={WIDTH} height={HEIGHT / 2} className="wg-black-zone" />
        <rect x="0" y={HEIGHT / 2} width={WIDTH} height={HEIGHT / 2} className="wg-white-zone" />
        <line x1="0" y1={HEIGHT / 2} x2={WIDTH} y2={HEIGHT / 2} className="wg-midline" />

        {segments.map((segment, i) => (
          <polyline key={i} points={segment} className="wg-line" />
        ))}

        {points.map((point, i) =>
          point.scoreLeadBlack === null ? null : (
            <circle
              key={i}
              cx={x(i)}
              cy={y(point.scoreLeadBlack)}
              r={i === currentIndex ? 4 : swingSet.has(i) ? 3.5 : 2}
              className={`wg-dot${i === currentIndex ? " current" : ""}${swingSet.has(i) ? " swing" : ""}`}
            />
          )
        )}

        {/* Full-height click targets so any move (analyzed or not) can be selected. */}
        {points.map((point, i) => (
          <rect
            key={`hit-${i}`}
            x={x(i) - (n <= 1 ? innerW / 2 : innerW / (n - 1) / 2)}
            y="0"
            width={n <= 1 ? innerW : innerW / (n - 1)}
            height={HEIGHT}
            className="wg-hit"
            onClick={() => onSelect(i)}
            onFocus={() => setHoveredIndex(point.scoreLeadBlack === null ? null : i)}
            onBlur={() => setHoveredIndex(null)}
            onPointerEnter={() => setHoveredIndex(point.scoreLeadBlack === null ? null : i)}
            onPointerLeave={() => setHoveredIndex(null)}
            tabIndex={0}
          >
            <title>{`${point.label}${point.scoreLeadBlack === null ? "" : ` — ${formatScoreLead(point.scoreLeadBlack)}`}`}</title>
          </rect>
        ))}
      </svg>
    </div>
  );
}
